import { Button } from 'components';
import { Dialog, DialogHeader, DialogTitle } from 'components/dialog/dialog';
import { useTranslation } from 'react-i18next';
import { useAudioDevice } from 'providers/AudioDeviceProvider';
import {
  SupportHint,
  TroubleshootingSteps,
  blockKeyForState,
  troubleshootingBlock,
} from 'widgets/audio';
import { deviceNames } from 'widgets/audio/deviceNames';
import { StepBadge } from 'widgets/audio/StepBadge';
import {
  RemoteStep,
  RemoteStepBody,
  RemoteStepBullets,
  RemoteStepOption,
  RemoteStepOptions,
  RemoteSteps,
  RemoteStepTitle,
  WarningDialogContent,
  WarningDialogDescription,
  WarningDialogDivider,
} from './NoAudioWarningDialog.styles';

interface NoAudioWarningDialogProps {
  isOpen: boolean;
  onDismiss: () => void;
}

const MutedP = ({ children }: { children: React.ReactNode }) => (
  <p className="text-sm text-muted-foreground">{children}</p>
);

const MutedLi = ({ children }: { children: React.ReactNode }) => (
  <li className="text-sm text-muted-foreground">{children}</li>
);

const RemoteDesktopSteps = () => {
  const { t } = useTranslation();
  return (
    <RemoteSteps>
      <RemoteStep>
        <StepBadge number={1} />
        <RemoteStepBody>
          <RemoteStepTitle>{t('recording.noAudioWarning.step1Title')}</RemoteStepTitle>
          <MutedP>{t('recording.noAudioWarning.step1Description')}</MutedP>
        </RemoteStepBody>
      </RemoteStep>

      <RemoteStep>
        <StepBadge number={2} />
        <RemoteStepBody>
          <RemoteStepTitle>{t('recording.noAudioWarning.step2Title')}</RemoteStepTitle>
          <RemoteStepBullets>
            <MutedLi>{t('recording.noAudioWarning.step2Bullet1')}</MutedLi>
            <MutedLi>{t('recording.noAudioWarning.step2Bullet2')}</MutedLi>
            <MutedLi>{t('recording.noAudioWarning.step2Bullet3')}</MutedLi>
          </RemoteStepBullets>
        </RemoteStepBody>
      </RemoteStep>

      <RemoteStep>
        <StepBadge number={3} />
        <RemoteStepOptions>
          <RemoteStepTitle>{t('recording.noAudioWarning.step3Title')}</RemoteStepTitle>
          <RemoteStepOption>
            <RemoteStepTitle>{t('recording.noAudioWarning.step3RemoteTitle')}</RemoteStepTitle>
            <MutedP>{t('recording.noAudioWarning.step3RemoteDescription')}</MutedP>
          </RemoteStepOption>
          <RemoteStepOption>
            <RemoteStepTitle>{t('recording.noAudioWarning.step3LocalTitle')}</RemoteStepTitle>
            <MutedP>{t('recording.noAudioWarning.step3LocalDescription')}</MutedP>
          </RemoteStepOption>
        </RemoteStepOptions>
      </RemoteStep>
    </RemoteSteps>
  );
};

// The dialog may close by itself once sound is back; the button that opened it is gone by then.
const keepFocusOnPage = (event: Event) => {
  event.preventDefault();
  queueMicrotask(() => {
    if (document.activeElement === document.body) {
      document.querySelector<HTMLElement>('main')?.focus();
    }
  });
};

export const NoAudioWarningDialog = ({ isOpen, onDismiss }: NoAudioWarningDialogProps) => {
  const { t } = useTranslation();
  const audio = useAudioDevice();
  const blockKey =
    audio.mode === 'active' && audio.state !== 'passive' ? blockKeyForState(audio.state) : null;
  const steps =
    audio.mode !== 'active' ? (
      <RemoteDesktopSteps />
    ) : blockKey ? (
      <TroubleshootingSteps block={troubleshootingBlock(blockKey, t)} />
    ) : null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onDismiss()}>
      <WarningDialogContent onCloseAutoFocus={keepFocusOnPage}>
        <DialogHeader>
          <DialogTitle>{t('recording.noAudioWarning.overlayTitle')}</DialogTitle>
          <WarningDialogDescription $masked={!steps}>
            {steps
              ? t('recording.noAudioWarning.overlaySubtitle')
              : t('recording.noAudioWarning.overlaySubtitleNoSteps', deviceNames(audio))}
          </WarningDialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {steps}

          {steps && <WarningDialogDivider />}

          <SupportHint />
        </div>

        <Button variant="secondary" onClick={onDismiss} className="w-full mt-2">
          {t('recording.noAudioWarning.dismiss')}
        </Button>
      </WarningDialogContent>
    </Dialog>
  );
};
